import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'

interface Event {
  id: number
  title: string
  subtitle?: string
  date: string
  googledrivelink: string
  location: string
  image: string
  description?: string
  ticket_url?: string
}

interface EventsCarouselProps {
  events: Event[]
  autoPlayInterval?: number
}

const EventsCarousel = ({ events, autoPlayInterval = 5000 }: EventsCarouselProps) => {
  const navigate = useNavigate()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  useEffect(() => {
    if (!isAutoPlaying || events.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length)
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [isAutoPlaying, events.length, autoPlayInterval])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % events.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  if (events.length === 0) {
    return (
      <div className="w-full py-16 bg-gray-800 flex items-center justify-center">
        <p className="text-gray-400">No events available</p>
      </div>
    )
  }

  return (
    <div className="relative w-full py-12 bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
              Events
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-2">
              Upcoming Events
            </h2>
          </div>
          <Link
            to="/events"
            className="hidden md:block bg-gray-800 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-block"
          >
            View All Events
          </Link>
        </div>

        <div className="relative overflow-hidden rounded-lg">
          <div className="relative h-auto">
            {events.map((event, index) => (
              <div
                key={event.id}
                onClick={() => {
                  navigate(`/events/${event.id}`)
                }}
                className={`transition-opacity duration-500 cursor-pointer ${
                  index === currentIndex ? 'opacity-100 block' : 'opacity-0 hidden'
                }`}
              >
                <div className="grid md:grid-cols-2 bg-gray-800 rounded-lg overflow-hidden">
                  <div className="relative">
                    <img
                      src={event.image}
                      alt={event.title}
                    />
                    <div className="absolute top-4 left-4 bg-primary-light text-white px-4 py-2 rounded-lg font-semibold">
                      {event.date}
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {event.title}
                    </h3>
                    {event.subtitle && (
                      <p className="text-primary-light text-lg md:text-xl mb-4">
                        {event.subtitle}
                      </p>
                    )}
                    <div className="flex items-center text-gray-400 mb-4">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{event.location}</span>
                    </div>
                    {event.description && (
                      <p className="text-gray-300 mb-6">
                        {event.description}
                      </p>
                    )}
                    <a
                      onClick={(e) => e.stopPropagation()}
                      href={event.ticket_url || "https://www.ticketmaster.com/event/2D006375A9CAB6E2"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors w-full md:w-auto inline-block text-center"
                    >
                      Buy Tickets
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {events.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-300 z-10"
                aria-label="Previous event"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all duration-300 z-10"
                aria-label="Next event"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Dots Indicator */}
        {events.length > 1 && (
          <div className="flex justify-center mt-8 space-x-2">
            {events.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 bg-primary-light'
                    : 'w-2 bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Go to event ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default EventsCarousel

